import { zodResolver } from '@hookform/resolvers/zod';
import { VisuallyHidden } from '@reach/visually-hidden';
import { graphql, useStaticQuery } from 'gatsby';
import { find } from 'ramda';
import React, { FC, useContext, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import styled from 'styled-components';
import { z } from 'zod';

import BasicText from '@ui/atoms/BasicText';
import Button, { IButtonProps } from '@ui/atoms/Button';
import FlexColumn, { IFlexColumn } from '@ui/atoms/FlexColumn';
import NavigationSection from '@ui/atoms/NavigationSection';
import TextButton from '@ui/atoms/TextButton';

import StateSelector from '@ui/forms/StateSelector';
import TextInput from '@ui/forms/TextInput';
import ZipTextInput from '@ui/forms/ZipTextInput';

import SubmitButton, { ISubmitButtonProps } from '@ui/molecules/SubmitButton';

import fullWidth from '@ui/styled/FullWidth';
import spaced from '@ui/styled/Spaced';

import { commonErrors } from '@utils/error';
import { update } from '@utils/indexedDb';
import { requiredString } from '@utils/validation';

import FlowContext from '@providers/FlowContextProvider';

import usStates from '@data/US_States';

import { IFlowStepProps } from '@folx-types/flow-steps';

import Add from '@assets/vectors/add.inline.svg';

import theme from '@theme/FolxTheme';

interface IHomeAddressContent {
	pageContent: {
		address_label: string;
		address_line_1_placeholder: string;
		address_line_2_placeholder: string;
		add_address_line_2_label: string;
		city_label: string;
		city_placeholder: string;
		state_label: string;
		zip_code_label: string;
		zip_code_placeholder: string;
	};
}

interface IHomeAddressStepProps extends IFlowStepProps {}

const FullWidthForm = fullWidth(spaced('form'));
const FullWidthColumn = fullWidth<IFlexColumn>(FlexColumn);
const SpacedColumn = spaced<IFlexColumn>(FlexColumn);
const FullWidthButton = fullWidth<IButtonProps>(Button);
const FullWidthSubmitButton = fullWidth<ISubmitButtonProps>(SubmitButton);

const StyledStateInput = styled(StateSelector)`
	${BasicText} {
		margin-bottom: ${theme.spacing.xs};
	}
`;

export const addressSchema = z.object({
	shippingAddressLine1: z.string().min(1),
	shippingAddressLine2: z
		.string()
		.nullish()
		.transform((x) => x ?? undefined),
	shippingCity: z.string().min(1),
	shippingState: z
		.string()
		.refine(
			(x) => x !== 'default' && !!usStates.find(({ value }: { value: string }) => value === x)
		),
	shippingZip: z.string().regex(/^(?!0{5})(\d{5})(?!-?0{4})(-\d{4})?$/),
});

const HomeAddressStep: FC<IHomeAddressStepProps> = ({ stepKey, bailout = () => false }) => {
	const context = useContext(FlowContext);
	const { handleFormSubmission, handleNavBack, existingFormData, destinationFlow } = context;

	const [submitting, setSubmitting] = useState(false);
	const [showAptLine, setShowAptLine] = useState(false);

	type Schema = Partial<z.input<typeof addressSchema>>;

	const {
		register,
		control,
		handleSubmit,
		formState: { errors, isSubmitted },
	} = useForm<Schema>({
		defaultValues: {
			...existingFormData,
			shippingState: existingFormData?.shippingState || existingFormData?.stateOfResidence,
		},
		resolver: zodResolver(addressSchema),
		mode: 'onChange',
	});

	const { pageContent } = useStaticQuery<IHomeAddressContent>(
		graphql`
			{
				pageContent: butterPage(slug: { eq: "home-address" }) {
					address_label
					address_line_1_placeholder
					address_line_2_placeholder
					add_address_line_2_label
					city_label
					city_placeholder
					state_label
					zip_code_label
					zip_code_placeholder
				}
			}
		`
	);
	const {
		address_label,
		address_line_1_placeholder,
		address_line_2_placeholder,
		add_address_line_2_label,
		city_label,
		city_placeholder,
		state_label,
		zip_code_label,
		zip_code_placeholder,
	} = pageContent;

	const onSubmit = async (data) => {
		const shouldBailout = bailout(data, destinationFlow) as boolean;
		if (shouldBailout) {
			await update('user', {
				email: data.email,
				stateOfResidence: data.shippingState,
			});
		}
		setSubmitting(true);
		handleFormSubmission(data, stepKey).finally(() => {
			setSubmitting(false);
		});
	};

	const aptLine = (
		<TextInput
			placeholder={address_line_2_placeholder}
			{...register('shippingAddressLine2')}
			fullWidth
		/>
	);

	return (
		<FullWidthForm onSubmit={handleSubmit(onSubmit)} marginBottom="xxxl">
			<SpacedColumn gap="sm" marginBottom="lgxl">
				<FullWidthColumn gap="xs">
					<TextInput
						placeholder={address_line_1_placeholder}
						label={address_label}
						error={isSubmitted && !!errors.shippingAddressLine1}
						errorLabel={errors?.shippingAddressLine1?.message && commonErrors.inputRequired}
						{...register('shippingAddressLine1')}
						fullWidth
					/>
				</FullWidthColumn>
				{!showAptLine && (
					<TextButton
						label={add_address_line_2_label}
						Icon={Add}
						variant="text"
						underline
						onClick={() => setShowAptLine(true)}
					/>
				)}
				{showAptLine ? aptLine : <VisuallyHidden>{aptLine}</VisuallyHidden>}
				<FullWidthColumn gap="xs">
					<TextInput
						placeholder={city_placeholder}
						label={city_label}
						error={isSubmitted && !!errors.shippingCity}
						errorLabel={errors?.shippingCity?.message && commonErrors.inputRequired}
						{...register('shippingCity')}
					/>
				</FullWidthColumn>
				<Controller
					name="shippingState"
					control={control}
					render={({ field }) => (
						<StyledStateInput
							error={isSubmitted && !!errors.shippingState}
							errorLabel={commonErrors.selectionRequired}
							disabled={!!existingFormData.stateOfResidence}
							displayValues
							label={state_label}
							{...field}
						/>
					)}
				/>
				<FullWidthColumn gap="xs">
					<ZipTextInput
						placeholder={zip_code_placeholder}
						label={zip_code_label}
						error={isSubmitted && !!errors.shippingZip}
						errorLabel={errors?.shippingZip?.message && commonErrors.inputRequired}
						{...register('shippingZip')}
					/>
				</FullWidthColumn>
			</SpacedColumn>
			<NavigationSection>
				<FullWidthButton
					label="Back"
					type="button"
					dataCy="Back"
					onClick={() => handleNavBack(stepKey)}
				/>
				<FullWidthSubmitButton
					label="Next"
					variant="secondary"
					submitting={submitting}
					dataCy="Next"
				/>
			</NavigationSection>
		</FullWidthForm>
	);
};

export default HomeAddressStep;
